import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { products } from "../config/products";
import "./Checkout.css";

const acceptedCoins = [
  { name: "USDT TRC20", network: "TRX" },
  { name: "USDT ERC20", network: "ETH" },
  { name: "USDT BSC", network: "BSC" },
  { name: "USDC ERC20", network: "ETH" },
  { name: "USDC Polygon", network: "POLYGON" },
  { name: "USDC Solana", network: "SOLANA" },
  { name: "TUSD ERC20", network: "ETH" },
  { name: "USDP", network: "ETH" },
];

function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const currency = searchParams.get("currency") || "EUR";
  const productSlug = searchParams.get("product") || "nis2scan";
  const product =
    products.find((p) => p.slug === productSlug) ||
    products.find((p) => p.slug === "nis2scan");
  // Use either product.priceUSD/EUR (if available), or pricing[0].price for enterprise tiers
  const priceEUR =
    product.priceEUR || (product.pricing ? product.pricing[0].price : 0);
  const priceUSD = product.priceUSD || Math.round(priceEUR * 1.1);
  const price = currency === "USD" ? priceUSD : priceEUR;

  // Use this to set the product name in enterprise contact section
  const productNameForTitle = `${product.name} - ${product.tagline}`;
  const symbol = currency === "USD" ? "$" : "\u20AC";

  const orderId = searchParams.get("order_id");
  const status = searchParams.get("status");

  if (orderId && status)
    return <CheckoutResult orderId={orderId} status={status} />;

  // Gate: products above $500 require contact sales
  if (priceUSD > 500) {
    return (
      <>
        <Helmet>
          <title>Checkout — Enterprise Purchase | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Enterprise purchase inquiry for enterprise-grade security and infrastructure tools."
          />
        </Helmet>
        <div className="checkout-page">
          <div className="checkout">
            <h1 className="checkout__title">Enterprise Purchase</h1>
            <p className="checkout__text">
              {productNameForTitle} requires a conversation. Contact our sales
              team for pricing, deployment options, and licensing.
            </p>
            <a
              href={`mailto:axiomlabssolutions@gmail.com?subject=Enterprise Inquiry: ${encodeURIComponent(productNameForTitle)}`}
              className="checkout__btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                textDecoration: "none",
                width: "auto",
                padding: "0.9rem 2rem",
              }}
            >
              Contact Sales →
            </a>
          </div>
        </div>
      </>
    );
  }

  const handlePurchase = async () => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setLoading(true);
    setError("");
    const newOrderId = crypto.randomUUID();
    const verificationToken = crypto
      .randomUUID()
      .replace(/-/g, "")
      .slice(0, 16);

    const { error: dbError } = await supabase.from("orders").insert({
      user_id: user.id,
      product_slug: productSlug,
      product_name: productNameForTitle,
      amount: price,
      currency,
      order_id: newOrderId,
      status: "pending",
      customer_email: user.email,
      verification_token: verificationToken,
    });
    if (dbError) {
      setError(dbError.message);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch("/create-invoice", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productSlug,
          priceCurrency: currency,
          customerEmail: user.email,
          orderId: newOrderId,
          verificationToken,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      await supabase
        .from("orders")
        .update({ nowpayments_id: data.nowpaymentsId })
        .eq("order_id", newOrderId);
      window.location.href = data.invoiceUrl;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (!user)
    return (
      <>
        <Helmet>
          <title>Checkout — Sign In Required | AxiomLabs Solutions</title>
          <meta
            name="description"
            content="Sign in to purchase AxiomLabs products securely with stablecoin payments."
          />
        </Helmet>
        <div className="checkout-page">
          <div className="checkout">
            <h1 className="checkout__title">Sign in to purchase</h1>
            <p className="checkout__text">You need to sign in.</p>
            <button className="checkout__btn" onClick={() => navigate("/auth")}>
              Sign In
            </button>
          </div>
        </div>
      </>
    );

  return (
    <>
      <Helmet>
        <title>
          Checkout — Purchase {product.name} | AxiomLabs Solutions
        </title>
        <meta
          name="description"
          content={`Complete your purchase of ${product.name} — ${product.tagline}. One-time purchase, owned forever.`}
        />
      </Helmet>
      <div className="checkout-page">
        <div className="checkout">
          <span className="checkout__label">One-Time Purchase</span>
          <h1 className="checkout__title">{product.name}</h1>
          <p className="checkout__price">
            {symbol}
            {price.toLocaleString()}
          </p>
          <p className="checkout__text">
            {productNameForTitle} — single purchase, owned forever.
          </p>

          <div className="checkout__coins">
            <p className="checkout__coins-title">Accepted Stablecoins</p>
            <div className="checkout__coins-list">
              {acceptedCoins.map((c, i) => (
                <span key={i} className="checkout__coin-tag">
                  {c.name}
                </span>
              ))}
            </div>
          </div>

          <ul className="checkout__features">
            {product.checkoutFeatures?.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>

          {error && <p className="checkout__error">{error}</p>}

          <button
            className="checkout__btn"
            onClick={handlePurchase}
            disabled={loading}
          >
            {loading
              ? "Creating payment..."
              : `Pay ${symbol}${price} with Stablecoin`}
          </button>

          <div className="checkout__powered">
            <span>Secured by</span>
            <strong>NOWPayments</strong>
          </div>
          <p className="checkout__note">
            You will be redirected to a secure checkout page.
          </p>
        </div>
      </div>
    </>
  );
}

function CheckoutResult({ orderId, status }) {
  const navigate = useNavigate();
  const isSuccess = status === "success";
  return (
    <>
      <Helmet>
        <title>
          {isSuccess ? "Payment Submitted!" : "Payment Cancelled"} | AxiomLabs
          Solutions
        </title>
        <meta
          name="description"
          content={
            isSuccess
              ? "Your payment has been submitted successfully. License key will be available soon."
              : "Your payment was cancelled. No charges were made."
          }
        />
      </Helmet>
      <div className="checkout-page">
        <div className={`checkout checkout--${status}`}>
          <div
            className={`checkout__icon ${isSuccess ? "checkout__icon--success" : "checkout__icon--cancelled"}`}
          >
            {isSuccess ? "\u2713" : "\u2715"}
          </div>
          <h1 className="checkout__title">
            {isSuccess ? "Payment Submitted!" : "Payment Cancelled"}
          </h1>
          <p className="checkout__text">
            {isSuccess
              ? "Processing. License key available soon."
              : "No charges were made."}
          </p>
          {isSuccess && <p className="checkout__order-id">Order: {orderId}</p>}
          <button className="checkout__btn" onClick={() => navigate("/auth")}>
            Back to Account
          </button>
          <div className="checkout__powered">
            <span>Secured by</span>
            <strong>NOWPayments</strong>
          </div>
        </div>
      </div>
    </>
  );
}

export default Checkout;
